import {
  getBirdviewDocFlows,
  getBirdviewMyActivities,
  getBirdviewMyCommunications,
  getBirdviewPortfolioOverview,
  getBirdviewSummary,
  getBirdviewTeamLoad,
} from '../../../../api'
import { useInitData } from '../../../../hooks'
import { BirdviewSection } from '../../DashboardBirdview.constants'
import { BIRDVIEW_GRETA_SECTION_MOCK } from '../../DashboardBirdview.mock'
import {
  checkIsBirdviewChatsViewable,
  checkIsBirdviewDocFlowsViewable,
  checkIsBirdviewDwellerDocumentsViewable,
  checkIsBirdviewDwellerSkeleton,
  checkIsBirdviewLandlordRole,
  checkIsBirdviewOversightSkeleton,
} from '../../DashboardBirdview.utils'
import {
  getBirdviewFirstPageParams,
  getBirdviewWidgetPageParams,
  toBirdviewActivityItem,
  toBirdviewListSection,
} from '../../DashboardBirdview.utils.api'
import { useBirdviewAttentionLoaders } from '../useBirdviewAttentionLoaders'
import { useBirdviewDwellerLoaders } from '../useBirdviewDwellerLoaders'
import { useBirdviewRequestLoaders } from '../useBirdviewRequestLoaders'

import {
  getBirdviewDocFlowsFilters,
  toBirdviewCommunicationItem,
  toBirdviewDocFlowItem,
  toBirdviewPortfolioItem,
  toBirdviewSummaryTiles,
  toBirdviewTeamLoadItem,
} from './useBirdviewSectionLoaders.utils'

export function useBirdviewSectionLoaders(role) {
  const { sections: sectionsPermissions } = useInitData()

  const { getMyCriticalRequestsSection, getMyServiceRequestsSection } =
    useBirdviewRequestLoaders()
  const { getAttentionSection } = useBirdviewAttentionLoaders()
  const {
    getDwellerAnnouncementsSection,
    getDwellerDocumentsSection,
    getDwellerHomeSection,
    getDwellerMessagesSection,
    getDwellerObjectsSection,
    getDwellerRequestsSection,
  } = useBirdviewDwellerLoaders()

  async function getTilesSection({ scope }) {
    const { data } = await getBirdviewSummary({ scope })

    return toBirdviewSummaryTiles(data)
  }

  async function getMyActivitiesSection(params) {
    const { data } = await getBirdviewMyActivities(
      getBirdviewWidgetPageParams(params)
    )

    return toBirdviewListSection(data, toBirdviewActivityItem)
  }

  async function getCommunicationSection() {
    const { data } = await getBirdviewMyCommunications(
      getBirdviewFirstPageParams()
    )

    return toBirdviewListSection(data, toBirdviewCommunicationItem)
  }

  async function getPortfolioSection(params) {
    const { data } = await getBirdviewPortfolioOverview(
      getBirdviewWidgetPageParams(params)
    )

    return toBirdviewListSection(data, toBirdviewPortfolioItem)
  }

  async function getTeamLoadSection(params) {
    const { data } = await getBirdviewTeamLoad(
      getBirdviewWidgetPageParams(params)
    )

    return toBirdviewListSection(data, toBirdviewTeamLoadItem)
  }

  // TODO Drop BIRDVIEW_GRETA_SECTION_MOCK once the suggestions endpoint ships.
  async function getGretaSection() {
    return BIRDVIEW_GRETA_SECTION_MOCK
  }

  async function getDocFlowsSection() {
    const { data } = await getBirdviewDocFlows(getBirdviewDocFlowsFilters())

    return toBirdviewListSection(data, toBirdviewDocFlowItem)
  }

  // Messages and Documents are optional dweller sections, gated by the same
  // section permissions as the menu.
  const dwellerGetters = {
    [BirdviewSection.DwellerAnnouncements]: getDwellerAnnouncementsSection,
    ...(checkIsBirdviewDwellerDocumentsViewable(sectionsPermissions)
      ? { [BirdviewSection.DwellerDocuments]: getDwellerDocumentsSection }
      : null),
    ...(checkIsBirdviewChatsViewable(sectionsPermissions)
      ? { [BirdviewSection.DwellerMessages]: getDwellerMessagesSection }
      : null),
    [BirdviewSection.DwellerRequests]: getDwellerRequestsSection,
    ...(checkIsBirdviewLandlordRole(role)
      ? { [BirdviewSection.DwellerObjects]: getDwellerObjectsSection }
      : { [BirdviewSection.DwellerHome]: getDwellerHomeSection }),
  }

  const staffGetters = {
    [BirdviewSection.Activities]: getMyActivitiesSection,
    [BirdviewSection.Attention]: getAttentionSection,
    [BirdviewSection.Communication]: getCommunicationSection,
    ...(checkIsBirdviewDocFlowsViewable(sectionsPermissions)
      ? { [BirdviewSection.DocFlows]: getDocFlowsSection }
      : null),
    [BirdviewSection.Greta]: getGretaSection,
    [BirdviewSection.Properties]: getPortfolioSection,
    [BirdviewSection.Tiles]: getTilesSection,
    ...(checkIsBirdviewOversightSkeleton(role)
      ? {
          [BirdviewSection.CriticalRequests]: getMyCriticalRequestsSection,
          [BirdviewSection.TeamLoad]: getTeamLoadSection,
        }
      : { [BirdviewSection.ServiceRequests]: getMyServiceRequestsSection }),
  }

  return {
    getters: checkIsBirdviewDwellerSkeleton(role)
      ? dwellerGetters
      : staffGetters,
  }
}
